/**
 * RequestsWidget — requests created over time, with a year-over-year overlay.
 *
 * Driven by the global date range:
 *   - Single day (the "Today" quick action, or a custom from==to) → grouped
 *     bar: This Year vs the same day Last Year.
 *   - Multi-day range → two daily trend lines: This Year vs the same calendar
 *     dates one year earlier (x-axis is the current-year date).
 *
 * Ports the old dashboard's "Requests" line chart (counts of request_parent
 * by creation date) and adds the YoY comparison.
 */

import * as React from "react";
import { useQueryClient } from "react-query";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { WidgetCard, RefreshButton } from "components/dashboard";
import { useRequestsTrend, formatQueryError } from "../hooks";
import type { LayoutItem, WidgetConfig } from "../types";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

const THIS_YEAR_COLOR = "rgb(59,130,246)"; // blue-500
const LAST_YEAR_COLOR = "rgb(156,163,175)"; // gray-400
const CHART_HEIGHT = 180;

/** "YYYY-MM-DD" → "MM/DD" for compact axis labels. */
function mmdd(isoDate: string): string {
  const [, m, d] = isoDate.split("-");
  return m && d ? `${m}/${d}` : isoDate;
}

export function RequestsWidget({ widget, layout }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useRequestsTrend();

  const chartData = (data?.points ?? []).map((p) => ({
    label: mmdd(p.date),
    thisYear: p.count,
    lastYear: p.priorCount,
  }));

  const isDay = data?.mode === "day";

  return (
    <WidgetCard
      title={widget.title}
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <RefreshButton
          onClick={() => queryClient.invalidateQueries(["dashboard", "requests-trend"])}
        />
      }
    >
      {chartData.length === 0 ? (
        <div className="flex items-center justify-center text-xs text-gray-400 py-8">
          No requests in this date range
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          {isDay ? (
            <BarChart data={chartData} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
                width={28}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                cursor={{ fill: "rgba(0,0,0,0.04)" }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="thisYear" name="This Year" fill={THIS_YEAR_COLOR} radius={[2, 2, 0, 0]} />
              <Bar dataKey="lastYear" name="Last Year" fill={LAST_YEAR_COLOR} radius={[2, 2, 0, 0]} />
            </BarChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
                minTickGap={16}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
                width={28}
                allowDecimals={false}
              />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line
                type="monotone"
                dataKey="thisYear"
                name="This Year"
                stroke={THIS_YEAR_COLOR}
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="lastYear"
                name="Last Year"
                stroke={LAST_YEAR_COLOR}
                strokeWidth={2}
                strokeDasharray="4 2"
                dot={false}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      )}
    </WidgetCard>
  );
}

const TOOLTIP_STYLE: React.CSSProperties = {
  fontSize: 12,
  border: "1px solid #e5e7eb",
  borderRadius: 4,
  padding: "4px 8px",
};
